import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ChatState from "../../../../../components/chat/chatState/ChatState";
import AlertCircleIcon from "../../../../../components/icons/AlertCircleIcon";
import CloseIcon from "../../../../../components/icons/CloseIcon";
import CustomerSupportSidebarIcon from "../../../../../components/icons/CustomerSupportSidebarIcon";
import SearchIcon from "../../../../../components/icons/SearchIcon";
import Loader from "../../../../../components/loader/Loader";
import Skeleton from "../../../../../components/skeleton/Skeleton";
import {
  fetchSupportChats,
  openSupportChat,
  refreshSupportChatsHead,
  setChatsPlan,
} from "../../../../../features/supportChat/supportChatSlice";
import useInfiniteScroll from "../../../../../hooks/useInfiniteScroll";
import styles from "./chatList.module.css";
import OneChat from "./oneChat/OneChat";

const SEARCH_DELAY_MS = 350;
const SKELETON_ROWS = 8;

const ListSkeleton = () => (
  <div className={styles.skeletonList} aria-hidden="true">
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <div key={index} className={styles.skeletonRow}>
        <Skeleton width={44} height={44} radius="50%" />
        <div className={styles.skeletonLines}>
          <Skeleton width={`${38 + ((index * 17) % 30)}%`} height={12} />
          <Skeleton width={`${62 + ((index * 23) % 32)}%`} height={10} />
        </div>
      </div>
    ))}
  </div>
);

// plan "prime": شات البرايم, Prime players' chats only. Another page (prize
// delivery) can link here with a player's name: the list opens on that search
// and that player's chat opens once.
const ChatList = ({ plan = "all" }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const linkedName = (location.state?.userName || "").trim();

  // What's typed, and the search it settles into.
  const [query, setQuery] = useState(linkedName);
  const [search, setSearch] = useState(linkedName);

  const chats = useSelector((state) => state.supportChat.chats);
  const status = useSelector((state) => state.supportChat.fetchAllChatsStatus);
  const requestPage = useSelector((state) => state.supportChat.chatsRequestPage);
  const listQuery = useSelector((state) => state.supportChat.chatsListQuery);
  const { page, hasMore } = useSelector((state) => state.supportChat.chatsPagination);
  const missedActivity = useSelector((state) => state.supportChat.chatsMissedActivity);
  const activeChatId = useSelector((state) => state.supportChat.activeChatId);
  const timeZone = useSelector((state) => state.timeZone.timeZone);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // This tab's list (setChatsPlan first: switching tabs starts clean).
  useEffect(() => {
    dispatch(setChatsPlan(plan));
    dispatch(fetchSupportChats({ page: 1, username: search }));
  }, [dispatch, plan, search]);

  // A player the list doesn't hold just wrote: bring its first page up to
  // date (once per burst).
  useEffect(() => {
    if (!missedActivity || search) return undefined;
    const timer = setTimeout(() => dispatch(refreshSupportChatsHead()), 1000);
    return () => clearTimeout(timer);
  }, [dispatch, missedActivity, search]);

  const linkedOpened = useRef(false);
  useEffect(() => {
    if (!linkedName || linkedOpened.current) return;
    if (status !== "succeeded" || listQuery !== linkedName) return;
    linkedOpened.current = true;
    const wanted = linkedName.toLowerCase();
    const chat =
      chats.find((entry) => entry.userId?.name?.toLowerCase() === wanted) || chats[0];
    if (chat) dispatch(openSupportChat(chat));
  }, [dispatch, linkedName, status, listQuery, chats]);

  const handleOpen = useCallback((chat) => dispatch(openSupportChat(chat)), [dispatch]);

  const loadMore = useCallback(() => {
    dispatch(fetchSupportChats({ page: page + 1, username: search }));
  }, [dispatch, page, search]);

  const reload = () => dispatch(fetchSupportChats({ page: 1, username: search }));

  const clearSearch = () => {
    setQuery("");
    setSearch("");
  };

  const scrollRef = useRef(null);
  useInfiniteScroll({
    ref: scrollRef,
    // A failed page waits for its retry button instead of re-firing on scroll.
    hasMore: hasMore && status !== "failed",
    isLoading: status === "loading",
    onLoadMore: loadMore,
  });

  const loading = status === "loading";
  const failed = status === "failed";
  const firstPagePending = loading && requestPage === 1;
  const showSkeleton = firstPagePending && chats.length === 0;
  // Rows from the previous search stay, faded, until the new results land.
  const stale = firstPagePending && listQuery !== search;
  const empty = status === "succeeded" && chats.length === 0;

  return (
    <div className={styles.chatList}>
      <div className={styles.searchBar}>
        <label className={styles.searchField}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            dir="auto"
            className={styles.searchInput}
            placeholder="ابحث باسم اللاعب أو معرّفه"
            aria-label="البحث في المحادثات"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") setSearch(query.trim());
              if (event.key === "Escape" && query) clearSearch();
            }}
          />
          {firstPagePending && search && <Loader size={16} />}
          {query && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={clearSearch}
              aria-label="مسح البحث"
            >
              <CloseIcon />
            </button>
          )}
        </label>
      </div>

      <div
        className={`${styles.progress} ${firstPagePending && chats.length > 0 ? styles.progressActive : ""}`}
        aria-hidden="true"
      />

      <div
        className={`${styles.scroller} ${stale ? styles.stale : ""}`}
        ref={scrollRef}
        aria-busy={loading}
      >
        {showSkeleton && <ListSkeleton />}

        {failed && requestPage === 1 && chats.length === 0 && (
          <ChatState
            tone="error"
            icon={<AlertCircleIcon />}
            title="تعذّر تحميل المحادثات"
            text="تحقق من الاتصال ثم حاول مرة أخرى."
            actionLabel="إعادة المحاولة"
            onAction={reload}
          />
        )}

        {failed && requestPage === 1 && chats.length > 0 && (
          <div className={styles.banner} role="alert">
            <span>تعذّر تحديث القائمة</span>
            <button type="button" className={styles.linkButton} onClick={reload}>
              إعادة المحاولة
            </button>
          </div>
        )}

        {empty &&
          (search ? (
            <ChatState
              icon={<SearchIcon />}
              title="لا توجد نتائج"
              text={`لا توجد محادثة للاعب يطابق «${search}».`}
            />
          ) : (
            <ChatState
              icon={<CustomerSupportSidebarIcon />}
              title={plan === "prime" ? "لا توجد محادثات برايم بعد" : "لا توجد محادثات بعد"}
              text="ستظهر هنا رسائل اللاعبين فور وصولها."
            />
          ))}

        {chats.map((chat) => (
          <OneChat
            key={chat._id}
            chat={chat}
            isActive={chat._id === activeChatId}
            onOpen={handleOpen}
            timeZone={timeZone}
          />
        ))}

        {loading && requestPage > 1 && (
          <div className={styles.footerRow}>
            <Loader size={18} />
            <span>جارٍ تحميل المزيد…</span>
          </div>
        )}

        {failed && requestPage > 1 && (
          <div className={styles.footerRow} role="alert">
            <span>تعذّر تحميل المزيد</span>
            <button type="button" className={styles.linkButton} onClick={loadMore}>
              إعادة المحاولة
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
